import { Stack, router } from 'expo-router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StatusBar, StyleSheet, View } from 'react-native';

import { RefreshButton } from '@/components/common/refresh-button';
import { ActivityIndicator, Button, Card, CardContent, Text, TextInput } from '@/components/ui';
import { DataTable } from '@/components/ui/data-table';
import { type ApprovalQueueKind, useWorkOrderQueue } from '@/hooks/use-approval-queue';
import { useAssociations, useUsers } from '@/hooks/use-data';
import { usePermissions } from '@/hooks/use-permissions';
import { useBulkApproveAll } from '@/hooks/use-work-order-review';
import { type WorkOrder } from '@/types/models';
import { Alert } from '@/utils/alert';
import { approvalSlaLabel, type ApprovalSlaStatus } from '@/utils/approval-sla';
import { formatBusinessDateUS, parseDateToMs } from '@/utils/date-utils';

/**
 * The review-chain queue, as a filterable table (#496) — shared by the Needs Review and
 * Needs Approval routes, which differ only in `kind`.
 *
 * ── ONE COMPONENT, TWO ROUTES ──
 *
 * The two queues used to share one ROUTE whose identity flipped by role, so an admin — who
 * may do both jobs — could only ever reach one. Now each route fixes `kind`, and this page
 * renders whichever it is handed. Everything that differs between them (the PM column, the
 * SLA clock, bulk approve, the date column, the empty-state copy) already branched on the
 * queue kind; it just reads a prop instead of the viewer's role.
 *
 * ── GATED AT THE ROUTE, NOT ONLY THE SIDEBAR ──
 *
 * The sidebar hides the rows a viewer cannot act on, but a bookmarked or emailed URL
 * bypasses the sidebar. So the page checks the same permission and sends an unentitled
 * viewer home — after permissions have LOADED, or a legitimate viewer bounces during the
 * flags window (see `Permissions.isLoading`).
 *
 * ── A TABLE, NOT CARDS ──
 *
 * The dashboard's To Do panel is a summary: eight rows, then "…and N more". This is where
 * you come to WORK the queue, which means seeing property, manager, age and cost in
 * columns you can sort, and selecting a batch. Accounting's paper sheet was a table for
 * the same reason.
 *
 * ── ADMINS FILTER BY MANAGER; MANAGERS DO NOT ──
 *
 * A property manager only ever sees their own associations, so a PM filter would be a
 * single-value dropdown. For an admin or accounting it is the question they actually have:
 * who is sitting on work. Both get the property filter.
 *
 * ── FILTERED IN MEMORY, DELIBERATELY ──
 *
 * The whole queue is fetched once and narrowed client-side rather than re-querying per
 * keystroke. Tying the filter to a network query key blanks the table on every character —
 * the stable-search-input rule this codebase already documents.
 */

interface Row {
	workOrder: WorkOrder;
	sla: ApprovalSlaStatus | null;
}

export const ReviewQueuePage: React.FC<{ kind: ApprovalQueueKind }> = ({ kind }) => {
	const permissions = usePermissions();
	const isReviewQueue = kind === 'needs_review';
	const allowed = isReviewQueue ? permissions.canViewReviewQueue : permissions.canViewApprovalQueue;

	// Redirect in an effect, once permissions have settled — never during render.
	useEffect(() => {
		if (!permissions.isLoading && !allowed) router.replace('/');
	}, [permissions.isLoading, allowed]);

	/**
	 * The SAME hook the sidebar badge and the dashboard tile use for this queue.
	 *
	 * It decides the scope (portfolio for reviewers and admins, own associations for
	 * everyone else), so this page can never show a different set from the badge that
	 * navigated here — which is exactly what happened when each surface passed its own
	 * scope argument.
	 */
	const queue = useWorkOrderQueue(kind, { enabled: allowed });
	const { rows, isLoading, refetch, overdueCount, label } = queue;
	/**
	 * Whether a PM COLUMN is worth showing — not whether the viewer is a PM.
	 *
	 * Anyone whose queue is portfolio-wide needs "who owes this"; somebody looking at their
	 * own associations would see one repeated name. The scope is what decides it.
	 */
	const showPmColumn = !queue.params.associationPmId;
	const { approveAll, isPending: approving } = useBulkApproveAll();

	const [search, setSearch] = useState('');
	const [pmFilter, setPmFilter] = useState<string | null>(null);
	const [assocFilter, setAssocFilter] = useState<string | null>(null);
	const [selected, setSelected] = useState<Row[]>([]);

	// Only needed to resolve names for the filter chips and the columns.
	const { data: assocResp } = useAssociations({ limit: 500 });
	const { data: usersResp } = useUsers({ limit: 200 });

	const assocById = useMemo(() => {
		const map = new Map<string, { name: string; assignedTo?: string }>();
		for (const a of ((assocResp?.data ?? []) as Array<{ id: string; name: string; assignedTo?: string }>)) {
			map.set(a.id, { name: a.name, assignedTo: a.assignedTo });
		}
		return map;
	}, [assocResp]);

	const userById = useMemo(() => {
		const map = new Map<string, string>();
		for (const u of ((usersResp?.data ?? []) as Array<{ id: string; name: string }>)) {
			map.set(u.id, u.name);
		}
		return map;
	}, [usersResp]);

	/** Flattened for the table, with the joined names the columns and filters both need. */
	const allRows = useMemo(() => (rows as Row[]).map((r) => {
		const assoc = r.workOrder.associationId ? assocById.get(r.workOrder.associationId) : undefined;
		const pmId = assoc?.assignedTo;
		return {
			...r,
			associationName: assoc?.name ?? '—',
			pmId: pmId ?? null,
			pmName: pmId ? (userById.get(pmId) ?? '—') : 'Unassigned',
		};
	}), [rows, assocById, userById]);

	/** Only the managers and properties actually present, so no filter yields nothing. */
	const pmOptions = useMemo(() => {
		const seen = new Map<string, string>();
		for (const r of allRows) if (r.pmId) seen.set(r.pmId, r.pmName);
		return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
	}, [allRows]);

	const assocOptions = useMemo(() => {
		const seen = new Map<string, string>();
		for (const r of allRows) {
			if (r.workOrder.associationId) seen.set(r.workOrder.associationId, r.associationName);
		}
		return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
	}, [allRows]);

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return allRows.filter((r) => {
			if (pmFilter && r.pmId !== pmFilter) return false;
			if (assocFilter && r.workOrder.associationId !== assocFilter) return false;
			if (!q) return true;
			return String(r.workOrder.orderNumber ?? '').includes(q)
				|| r.associationName.toLowerCase().includes(q)
				|| r.pmName.toLowerCase().includes(q)
				|| (r.workOrder.jobLocation ?? '').toLowerCase().includes(q)
				|| (r.workOrder.customerDescription ?? '').toLowerCase().includes(q);
		});
	}, [allRows, search, pmFilter, assocFilter]);

	const approveSelected = useCallback(async () => {
		if (selected.length === 0) return;
		try {
			const totals = await approveAll(selected.map((r) => r.workOrder.id));
			setSelected([]);
			await refetch();
			const parts = [`${totals.approved} approved`];
			// Counted apart from `approved`, so re-sending a stale selection cannot report
			// work it did not do.
			if (totals.skipped > 0) parts.push(`${totals.skipped} already approved`);
			if (totals.errors.length > 0) parts.push(`${totals.errors.length} failed`);
			Alert.alert(totals.errors.length > 0 ? 'Approved with errors' : 'Approved', parts.join(' · '));
		} catch (e) {
			await refetch();
			Alert.alert('Could not approve', (e as Error).message);
		}
	}, [selected, approveAll, refetch]);

	const nowMs = Date.now();

	const columns = useMemo(() => [
		{
			id: 'orderNumber', name: 'WO #', grow: 1, sortable: true,
			selector: (r: typeof allRows[number]) => r.workOrder.orderNumber ?? 0,
			cell: (r: typeof allRows[number]) => <Text>{r.workOrder.orderNumber ?? '—'}</Text>,
		},
		{
			id: 'association', name: 'Property', grow: 3, sortable: true,
			selector: (r: typeof allRows[number]) => r.associationName,
		},
		...(showPmColumn ? [{
			// A PM sees only their own, so the column would be one repeated name.
			id: 'pm', name: 'Property Manager', grow: 2, sortable: true,
			selector: (r: typeof allRows[number]) => r.pmName,
		}] : []),
		{
			id: 'location', name: 'Location', grow: 2,
			selector: (r: typeof allRows[number]) => r.workOrder.jobLocation ?? '—',
		},
		{
			id: 'description', name: 'Work Completed', grow: 4,
			selector: (r: typeof allRows[number]) => r.workOrder.customerDescription ?? '',
			cell: (r: typeof allRows[number]) => (
				<Text numberOfLines={2} style={styles.desc}>{r.workOrder.customerDescription || '—'}</Text>
			),
		},
		{
			/**
			 * TWO DIFFERENT WIRE FORMATS in one column, hence `parseDateToMs` and not `Number`.
			 *
			 * `reviewApprovedAt` goes out as a millisecond NUMBER — `utils/approval-sla.ts`
			 * consumes it directly — while `completedAt` goes out as an ISO STRING, like the
			 * older `pmApproved` beside it. `Number('2026-07-29T00:00:00.000Z')` is NaN, so
			 * `Number(...) || undefined` silently became `undefined` and every row in the
			 * review queue rendered "—" while the detail page showed the date perfectly.
			 *
			 * Only the REVIEW half was broken, which is why it survived review: the Sent
			 * column reads the numeric field and was always correct.
			 *
			 * `parseDateToMs` accepts a number, a numeric string and an ISO string, so this
			 * stops depending on which of the two the API happens to send.
			 */
			id: 'sentAt', name: isReviewQueue ? 'Completed' : 'Sent', grow: 1, sortable: true,
			selector: (r: typeof allRows[number]) =>
				parseDateToMs(isReviewQueue ? r.workOrder.completedAt : r.workOrder.reviewApprovedAt) ?? 0,
			cell: (r: typeof allRows[number]) => (
				<Text>{formatBusinessDateUS(
					parseDateToMs(isReviewQueue ? r.workOrder.completedAt : r.workOrder.reviewApprovedAt)
						?? undefined,
				)}</Text>
			),
		},
		// No SLA clock on the review queue: it starts when accounting hands the work over.
		...(isReviewQueue ? [] : [{
			id: 'due', name: 'Due', grow: 2, sortable: true,
			// Sorted on the DUE DATE, not the label — sorting the rendered string would put
			// "2 days overdue" next to "20 days overdue" alphabetically.
			selector: (r: typeof allRows[number]) => r.sla?.dueMs ?? 0,
			cell: (r: typeof allRows[number]) => (
				<Text style={r.sla?.overdue ? styles.overdue : undefined}>
					{r.sla ? approvalSlaLabel(r.sla, nowMs) : '—'}
				</Text>
			),
		}]),
	], [showPmColumn, isReviewQueue, nowMs]);

	const activeFilters = (pmFilter ? 1 : 0) + (assocFilter ? 1 : 0);

	// Nothing to show an unentitled viewer while the redirect above lands.
	if (!allowed) return null;

	return (<>
		<Stack.Screen
			options={{
				title: label,
				headerRight: () => Platform.OS === 'web' ? <RefreshButton onRefresh={() => void refetch()} /> : null,
			}}
		/>
		<StatusBar barStyle="dark-content" />

		<ScrollView style={styles.scroll} contentContainerStyle={styles.page}>
			<View style={styles.header}>
				<View style={{ flex: 1, minWidth: 0 }}>
					<Text style={styles.title}>{label}</Text>
					<Text style={styles.subtitle}>
						{filtered.length} work order{filtered.length === 1 ? '' : 's'}
						{isReviewQueue ? ' completed, awaiting review' : ' awaiting approval'}
						{overdueCount > 0 ? ` · ${overdueCount} overdue` : ''}
						{activeFilters > 0 ? ` · filtered from ${allRows.length}` : ''}
					</Text>
				</View>
			</View>

			<View style={styles.filterRow}>
				<View style={{ flex: 1, minWidth: 200 }}>
					<TextInput
						placeholder="Filter by WO #, property, manager, location…"
						value={search}
						onChangeText={setSearch}
						testID="input-approval-search"
					/>
				</View>
				{/* Chip filters rather than dropdowns: the option lists are short, and a
				    visible chip makes an active filter obvious. */}
				{showPmColumn && pmOptions.length > 1 && (
					<FilterChips
						label="PM"
						options={pmOptions}
						value={pmFilter}
						onChange={setPmFilter}
						testIDPrefix="filter-pm"
					/>
				)}
				{assocOptions.length > 1 && (
					<FilterChips
						label="Property"
						options={assocOptions}
						value={assocFilter}
						onChange={setAssocFilter}
						testIDPrefix="filter-assoc"
					/>
				)}
			</View>

			{/*
				Bulk approve only on the APPROVAL queue. A reviewer's next step is writing the
				customer-facing description, one work order at a time, in the accounting
				queue — there is no batch action, and offering one here would 403.
			*/}
			{!isReviewQueue && selected.length > 0 && (
				<Card style={styles.selectionCard}>
					<CardContent style={styles.selectionRow}>
						<Text style={styles.selectionLabel}>{selected.length} selected</Text>
						<Button
							mode="contained"
							onPress={approveSelected}
							disabled={approving}
							testID="btn-approve-selected"
						>
							<Text>
								{approving
									? 'Approving…'
									: `Approve ${selected.length} work order${selected.length === 1 ? '' : 's'}`}
							</Text>
						</Button>
					</CardContent>
				</Card>
			)}

			{isLoading ? (
				<ActivityIndicator style={{ marginTop: 24 }} />
			) : filtered.length === 0 ? (
				<Text style={styles.empty}>
					{allRows.length === 0
						? (isReviewQueue
							? 'Nothing to review — every completed work order has been sent for approval.'
							: 'Nothing is waiting on your approval.')
						: 'No work orders match these filters.'}
				</Text>
			) : (
				<DataTable
					columns={columns}
					data={filtered}
					striped highlightOnHover pointerOnHover
					defaultSortFieldId={isReviewQueue ? 'sentAt' : 'due'}
					defaultSortAsc
					onRowClicked={(r: typeof allRows[number]) =>
						router.push({ pathname: '/work-orders/[id]', params: { id: r.workOrder.id } })}
					selectableRows={!isReviewQueue}
					onSelectedRowsChange={
						({ selectedRows }: { selectedRows: Row[] }) => setSelected(selectedRows)
					}
					conditionalRowStyles={[
						{ when: (r: typeof allRows[number]) => !!r.sla?.overdue, style: { backgroundColor: '#fdecea' } },
					]}
					style={styles.table}
				/>
			)}
		</ScrollView>
	</>
	);
};

/** A label plus an "All / …" chip row. Short option lists, so no dropdown needed. */
const FilterChips: React.FC<{
	label: string;
	options: Array<[string, string]>;
	value: string | null;
	onChange: (v: string | null) => void;
	testIDPrefix: string;
}> = ({ label, options, value, onChange, testIDPrefix }) => (
	<View style={styles.chipGroup}>
		<Text style={styles.chipGroupLabel}>{label}</Text>
		<Pressable
			onPress={() => onChange(null)}
			style={[styles.chip, value === null && styles.chipActive]}
			testID={`${testIDPrefix}-all`}
		>
			<Text style={[styles.chipLabel, value === null && styles.chipLabelActive]}>All</Text>
		</Pressable>
		{options.map(([id, name]) => (
			<Pressable
				key={id}
				onPress={() => onChange(value === id ? null : id)}
				style={[styles.chip, value === id && styles.chipActive]}
				testID={`${testIDPrefix}-${id}`}
			>
				<Text numberOfLines={1} style={[styles.chipLabel, value === id && styles.chipLabelActive]}>
					{name}
				</Text>
			</Pressable>
		))}
	</View>
);

const styles = StyleSheet.create({
	/**
	 * The SCROLLVIEW gets `flex: 1`; its content container must NOT.
	 *
	 * Two mistakes were stacked here. The page was a plain `View`, so eighty-five rows
	 * simply clipped with no way to reach them. And `flex: 1` on what is now the content
	 * container would reintroduce it — a content container constrained to the viewport
	 * height has nothing to scroll, which is the classic RN-Web ScrollView trap. Same shape
	 * as app/accounting/work-order-billing.tsx, the other DataTable page.
	 */
	scroll: { flex: 1 },
	page: { padding: 16, gap: 12, paddingBottom: 40 },
	header: { flexDirection: 'row', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
	title: { fontSize: 22, fontWeight: '700' },
	subtitle: { color: '#666', fontSize: 13 },
	filterRow: { flexDirection: 'row', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
	chipGroup: { flexDirection: 'row', gap: 6, alignItems: 'center', flexWrap: 'wrap' },
	chipGroupLabel: { fontSize: 12, fontWeight: '700', color: '#607d8b' },
	chip: {
		paddingVertical: 5, paddingHorizontal: 10, borderRadius: 12,
		backgroundColor: '#eceff1', maxWidth: 190,
	},
	chipActive: { backgroundColor: '#1976d2' },
	chipLabel: { fontSize: 12, color: '#37474f' },
	chipLabelActive: { color: '#fff', fontWeight: '600' },
	selectionCard: { backgroundColor: '#e3f2fd' },
	selectionRow: {
		flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
		gap: 12, flexWrap: 'wrap',
	},
	selectionLabel: { fontWeight: '600' },
	table: { width: '100%' },
	desc: { fontSize: 13 },
	overdue: { color: '#c62828', fontWeight: '700' },
	empty: { color: '#888', fontStyle: 'italic', textAlign: 'center', padding: 24 },
});
